import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }             from '../../../api/api.service';
import { CompanyService }         from '../../../companies/company.service';
import { FileUploadOptionsModel } from './typings/file-upload.options';
import { S3OptionsModel }         from './typings/s3-options';
import { SessionService }         from '../../../sessions/session.service';

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  cleanup: any;
  request: any;

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private sessionService  : SessionService,
    private translateService: TranslateService
  ) {}

  clearRequest () : void {
    if ( this.request ) {
      this.request.abort();
    }

    if ( this.cleanup ) {
      this.cleanup();
    }
  }

  createS3Request ( file : File, options : S3OptionsModel ) : any {
    const fd      = new FormData();
    const request = new XMLHttpRequest();

    fd.append('acl'                    , options.acl);
    fd.append('Content-Type'           , options[ 'Content-Type' ]);
    fd.append('key'                    , options.key);
    fd.append('policy'                 , options.policy);
    fd.append('success_action_redirect', options.success_action_redirect);
    fd.append('x-amz-signature'        , options.x_amz_signature);
    fd.append('x-amz-algorithm'        , options[ 'x-amz-algorithm' ]);
    fd.append('x-amz-credential'       , options[ 'x-amz-credential' ]);
    fd.append('x-amz-date'             , options[ 'x-amz-date' ]);
    fd.append('file'                   , file);

    return { request, fd };
  }

  getExtension ( filename : string ) : string {
    const match = (/[.]/.exec(filename)) ? /[^.]+$/.exec(filename) : ['png'];

    return (match && match[ 0 ]) || 'png';
  }

  getS3BucketLink () : ng.IPromise<string> {
    return this.apiService.get('/company/s3_bucket_link');
  }

  getS3Options ( file : File, options : FileUploadOptionsModel ) : ng.IPromise<S3OptionsModel> {
    return this.isAdvertisement(options)
      ? this.apiService.get('/workshop_admin/s3_signature_for_ad_v4')
      : this.apiService.get('/company/s3_signature_v4', [
          options.type,
          this.getExtension(file.name)
        ]);
  }

  isAdvertisement ( options : FileUploadOptionsModel ) : boolean {
    return options.type === 'advertisement';
  }

  upload ( file : File, options : FileUploadOptionsModel ) : ng.IPromise<any> {
    return Promise.all([
      this.getS3BucketLink(),
      this.getS3Options(file, options)
    ])
    .then(([ s3BucketLink, s3Options ]) => new Promise((resolve, reject) => {
      const call = this.createS3Request(file, s3Options);

      this.request = call.request;

      const onLoad = ( e : ProgressEvent<EventTarget> ) => {
        const xhr = e.target as XMLHttpRequest;

        if ( xhr.status === 204 ) {
          Promise.resolve()
          .then(() => this.isAdvertisement(options)
            ? Promise.resolve(true)
            : this.apiService.post('/company/s3_path', {
                file_type: options.type,
                s3_link  : s3Options.key
              }))
          .then(() => this.sessionService.refresh())
          .then(() => {
            if ( options.updateVersion ) {
              this.companyService.setImageVersion(options.type);
            }

            this.cleanup();

            return resolve(true);
          });
        }
      };

      const onAbort = () => {
        this.cleanup();

        reject(this.translateService.instant('JS_SPACE.MESSAGES.UPLOAD_ABORTED'));
      };

      const onError = ( e: ProgressEvent<EventTarget> ) => {
        const xhr = e.target as XMLHttpRequest;

        this.cleanup();

        reject((xhr && (xhr as any).responseText) || this.translateService.instant('JS_SPACE.MESSAGES.UPLOAD_ERROR'));
      };

      this.cleanup = () => {
        this.request.removeEventListener('abort', onAbort);
        this.request.removeEventListener('error', onError);
        this.request.removeEventListener('load', onLoad);
      };

      this.request.addEventListener('abort', onAbort);
      this.request.addEventListener('error', onError);
      this.request.addEventListener('load', onLoad);

      this.request.open('POST', s3BucketLink, true);
      this.request.send(call.fd);
    }));
  }
}
